import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PaginationBar } from "@/components/PaginationBar";
import { useCursorPagination } from "@/hooks/useCursorPagination";
import { formatDateTime } from "@/lib/format";

type InvoiceEmail = components["schemas"]["AdminInvoiceEmailResponse"];
type InvoiceEmailStatus = NonNullable<components["schemas"]["InvoiceEmailStatus"]>;

interface InvoiceEmailsTableProps {
  organisationId?: string;
  invoiceId?: string;
}

const statusFilterOptions: { value: InvoiceEmailStatus | "any"; label: string }[] = [
  { value: "any", label: "All statuses" },
  { value: "Sent", label: "Sent" },
  { value: "Delivered", label: "Delivered" },
  { value: "Bounced", label: "Bounced" },
  { value: "SpamComplaint", label: "Spam Complaint" },
];

function statusVariant(status: string): "default" | "secondary" | "outline" | "destructive" {
  switch (status) {
    case "Delivered":
      return "default";
    case "Sent":
      return "secondary";
    case "Bounced":
    case "SpamComplaint":
      return "destructive";
    default:
      return "outline";
  }
}

function statusLabel(status: string): string {
  return status === "SpamComplaint" ? "Spam Complaint" : status;
}

export function InvoiceEmailsTable({ organisationId, invoiceId }: InvoiceEmailsTableProps) {
  const [status, setStatus] = useState<InvoiceEmailStatus | "any">("any");
  const pagination = useCursorPagination({ initialLimit: 20 });

  const [emails, setEmails] = useState<InvoiceEmail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const prevStatusRef = useRef(status);
  useEffect(() => {
    if (prevStatusRef.current !== status) {
      prevStatusRef.current = status;
      pagination.reset();
    }
  }, [status, pagination.reset]);

  useEffect(() => {
    let cancelled = false;

    async function fetchEmails() {
      setLoading(true);
      setError(null);
      const { data, error: apiError } = await api.GET("/admin/invoice-emails", {
        params: {
          query: {
            OrganisationId: organisationId,
            InvoiceId: invoiceId,
            Status: status === "any" ? undefined : status,
            Cursor: pagination.cursor,
            Limit: pagination.limit,
          },
        },
      });
      if (cancelled) return;
      if (apiError) {
        setError("Failed to load invoice emails.");
      } else {
        setEmails(data?.data ?? []);
        pagination.setPage({
          nextCursor: data?.meta?.cursors?.next,
          previousCursor: data?.meta?.cursors?.previous,
        });
      }
      setLoading(false);
    }

    fetchEmails();
    return () => {
      cancelled = true;
    };
  }, [organisationId, invoiceId, status, pagination.cursor, pagination.limit]);

  if (error) {
    return <p className="text-destructive">{error}</p>;
  }

  const colSpan = 11 - (organisationId ? 1 : 0) - (invoiceId ? 1 : 0);

  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        <Select value={status} onValueChange={(v) => setStatus(v as InvoiceEmailStatus | "any")}>
          <SelectTrigger className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {statusFilterOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {loading && <Spinner className="size-5" />}
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>ID</TableHead>
              {!invoiceId && <TableHead>Invoice</TableHead>}
              {!organisationId && <TableHead>Organisation</TableHead>}
              <TableHead>Recipient</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Message ID</TableHead>
              <TableHead>Sent</TableHead>
              <TableHead>Delivered</TableHead>
              <TableHead>Bounced</TableHead>
              <TableHead>Spam Complaint</TableHead>
              <TableHead>Bounce Detail</TableHead>
              <TableHead>Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {emails.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={colSpan} className="text-muted-foreground h-24 text-center">
                  No invoice emails found.
                </TableCell>
              </TableRow>
            ) : (
              emails.map((email) => (
                <TableRow key={email.id}>
                  <TableCell>
                    <Badge variant="secondary" className="font-mono text-xs">
                      {email.id.slice(0, 8)}...
                    </Badge>
                  </TableCell>
                  {!invoiceId && (
                    <TableCell>
                      <Link
                        to={`/invoices/${email.invoiceId}?organisationId=${email.organisationId}`}
                      >
                        <Badge
                          variant="secondary"
                          className="font-mono text-xs hover:bg-primary/20"
                        >
                          {email.invoiceId.slice(0, 8)}...
                        </Badge>
                      </Link>
                    </TableCell>
                  )}
                  {!organisationId && (
                    <TableCell>
                      <Link to={`/organisations/${email.organisationId}`}>
                        <Badge
                          variant="secondary"
                          className="font-mono text-xs hover:bg-primary/20"
                        >
                          {email.organisationId.slice(0, 8)}...
                        </Badge>
                      </Link>
                    </TableCell>
                  )}
                  <TableCell className="text-sm">{email.recipient}</TableCell>
                  <TableCell>
                    <Badge variant={statusVariant(email.status)}>{statusLabel(email.status)}</Badge>
                  </TableCell>
                  <TableCell>
                    {email.messageId ? (
                      <span className="font-mono text-xs text-muted-foreground">
                        {email.messageId.slice(0, 8)}...
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">{formatDateTime(email.sentAt)}</TableCell>
                  <TableCell className="text-sm">
                    {email.deliveredAt ? (
                      formatDateTime(email.deliveredAt)
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    {email.bouncedAt ? (
                      formatDateTime(email.bouncedAt)
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    {email.spamComplaintAt ? (
                      formatDateTime(email.spamComplaintAt)
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    {email.bounceType || email.bounceDescription ? (
                      <div className="space-y-0.5">
                        {email.bounceType && (
                          <Badge variant="outline" className="text-xs">
                            {email.bounceType}
                          </Badge>
                        )}
                        {email.bounceDescription && (
                          <div className="text-muted-foreground text-xs max-w-xs truncate">
                            {email.bounceDescription}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">{formatDateTime(email.createdAt)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <PaginationBar
        hasPrevious={pagination.hasPrevious}
        hasNext={pagination.hasMore}
        isLoading={loading}
        limit={pagination.limit}
        onPrevious={pagination.goPrevious}
        onNext={pagination.goNext}
        onLimitChange={pagination.setLimit}
      />
    </div>
  );
}
